import { ref, type Ref } from 'vue';

import { createSingleton } from '@deepidoo/deesplay/utils';

export type Languages = 'de-DE' | 'en-US' | 'es-ES' | 'fr-FR' | 'it-IT' | 'pt-PT' | 'zh-CN';
type Keys = 'agent-chat.title' | 'agent-chat.reconnecting' | 'agent-chat.welcome';

const translations: Record<Languages, Record<Keys, string>> = {
    'en-US': {
        'agent-chat.title': 'Chatbot',
        'agent-chat.reconnecting': 'Connection to chatbot lost. Retrying...',
        'agent-chat.welcome': `Hello, how can I help you today?
For now, I can:
- search organizations
- search tags
- search artists
- search, create, edit, delete, duplicate and add tracks to playlists
- search, create, edit, delete and add slots to plannings

Try me! You can speak in any language you want, I will answer you in the same language.`
    },
    'fr-FR': {
        'agent-chat.title': 'Chatbot',
        'agent-chat.reconnecting': 'Connexion au chatbot perdue. Reconnexion...',
        'agent-chat.welcome': `Bonjour, comment puis-je vous aider aujourd'hui ?
Pour l'instant, je peux :
- rechercher des organisations
- rechercher des tags
- rechercher des artistes
- rechercher, créer, modifier, supprimer, dupliquer et ajouter des pistes à des playlists
- rechercher, créer, modifier, supprimer et ajouter des slots à des plannings

Essayez-moi ! Vous pouvez parler dans n'importe quelle langue, je vous répondrai dans la même langue.`
    },
    'de-DE': {
        'agent-chat.title': 'Chatbot',
        'agent-chat.reconnecting': 'Verbindung zum Chatbot verloren. Wiederholen...',
        'agent-chat.welcome': `Hallo, wie kann ich Ihnen heute helfen?
Derzeit kann ich:
- nach Organisationen suchen
- nach Tags suchen
- nach Künstlern suchen
- nach Tracks suchen, erstellen, bearbeiten, löschen, duplizieren und zu Playlists hinzufügen
- nach Slots suchen, erstellen, bearbeiten, löschen und zu Plannings hinzufügen.

Versuche mich! Sie können in jeder Sprache sprechen, ich werde Ihnen in der gleichen Sprache antworten.`
    },
    'es-ES': {
        'agent-chat.title': 'Chatbot',
        'agent-chat.reconnecting': 'Se ha perdido la conexión con el chatbot. Reintentando...',
        'agent-chat.welcome': `Hola, ¿en qué puedo ayudarte hoy?
Por ahora, puedo:
- buscar organizaciones
- buscar etiquetas
- buscar artistas
- buscar, crear, editar, eliminar, duplicar y añadir pistas a playlists
- buscar, crear, editar, eliminar y añadir slots a plannings

¡Pruébame! Puedes hablar en cualquier idioma, te responderé en el mismo idioma.`
    },
    'it-IT': {
        'agent-chat.title': 'Chatbot',
        'agent-chat.reconnecting': 'Connessione al chatbot persa. Nuovo tentativo...',
        'agent-chat.welcome': `Ciao, come posso aiutarti oggi?
Per ora posso:
- cercare organizzazioni
- cercare tag
- cercare artisti
- cercare, creare, modificare, eliminare, duplicare e aggiungere tracce alle playlist
- cercare, creare, modificare, eliminare e aggiungere slot alle planning

Provami! Puoi parlare in qualsiasi lingua, ti risponderò nella stessa lingua.`
    },
    'pt-PT': {
        'agent-chat.title': 'Chatbot',
        'agent-chat.reconnecting': 'Ligação ao chatbot perdida. A tentar novamente...',
        'agent-chat.welcome': `Olá, como posso ajudar hoje?
Por agora, posso:
- pesquisar organizações
- pesquisar etiquetas
- pesquisar artistas
- pesquisar, criar, editar, eliminar, duplicar e adicionar faixas a playlists
- pesquisar, criar, editar, eliminar e adicionar slots a plannings

Experimente-me! Pode falar em qualquer idioma, responderei no mesmo idioma.`
    },
    'zh-CN': {
        'agent-chat.title': '聊天机器人',
        'agent-chat.reconnecting': '与聊天机器人的连接已断开。正在重试...',
        'agent-chat.welcome': `你好，今天有什么可以帮您的？
目前我可以：
- 搜索组织
- 搜索标签
- 搜索艺人
- 搜索、创建、编辑、删除、复制播放列表，并向其中添加曲目
- 搜索、创建、编辑、删除排期，并向其中添加时段

试试看吧！您可以用任意语言交流，我会用同样的语言回复。`
    }
};

export const useTranslations = createSingleton((): {
    languageCode: Ref<Languages>;
    tr: (key: Keys) => string;
} => {
    const languageCode = ref<Languages>('en-US');

    const tr = (key: Keys): string => {
        return translations[languageCode.value][key] || key;
    };

    return {
        languageCode,
        tr
    };
});
